/**
 * AuthProvider confirmation-gate predicates.
 *
 * Unverified sessions bounce to `/aguardando-confirmacao` (email) or
 * `/verificar-telefone` (WhatsApp OTP) from every other path, including
 * `/entrar`, `/cadastrar`, and `/aceitar-convite`. `/verificar-email` stays
 * mounted for both gates: the confirmation link's token is consumed there.
 * Do not add `/entrar` as an exemption — that would let unverified sessions
 * sit on login and weaken the invite/signup gate. "Voltar para login" must
 * sign out first (`leaveUnverifiedSession`).
 *
 * Phone OTP is the prerequisite gate: email confirmation and pending-terms
 * redirects are deferred while `phoneNumberVerified === false`, so those
 * gates cannot bounce against `/verificar-telefone` forever. Do not add
 * `/aceitar-termos` to the phone allowlist — that would let users accept
 * terms before completing OTP.
 */

/**
 * Pages that consume a verification token from the query string.
 *
 * Signup mints a session before email is verified, so both gates run while
 * the confirmation link is still unused. Bouncing `/verificar-email` drops
 * `?token=` before the page can call `/api/auth/verify-email`.
 */
const VERIFICATION_TOKEN_PATHS = ["/verificar-email"] as const;

/** Paths where an email-unverified session may remain. */
const EMAIL_UNVERIFIED_ALLOWED = new Set<string>([
  "/aguardando-confirmacao",
  ...VERIFICATION_TOKEN_PATHS,
]);

/** Paths where a phone-unverified (`phoneNumberVerified === false`) session may remain. */
const PHONE_UNVERIFIED_ALLOWED = new Set<string>([
  "/verificar-telefone",
  ...VERIFICATION_TOKEN_PATHS,
]);

/**
 * Phone OTP is still required. Hold the email-confirmation bounce until it
 * finishes, including while the user is already on `/verificar-telefone`.
 * Otherwise the email gate and the phone gate redirect each other forever.
 */
export function shouldDeferEmailConfirmationForPhone(
  phoneNumberVerified: boolean | null | undefined,
): boolean {
  return phoneNumberVerified === false;
}

/**
 * Phone OTP is still required. Hold the pending-terms redirect until it
 * finishes. Otherwise AuthProvider pushes `/aceitar-termos` from
 * `/verificar-telefone`, and the phone bounce immediately sends the user
 * back — an infinite redirect loop. Mirror of
 * {@link shouldDeferEmailConfirmationForPhone}.
 */
export function shouldDeferTermsAcceptanceForPhone(
  phoneNumberVerified: boolean | null | undefined,
): boolean {
  return phoneNumberVerified === false;
}

/**
 * Bounce when email is not verified, except on the confirmation page.
 */
export function shouldBounceUnverifiedToConfirmation(
  pathname: string | null | undefined,
  emailVerified: boolean | null | undefined,
): boolean {
  return !emailVerified && !EMAIL_UNVERIFIED_ALLOWED.has(pathname ?? "");
}

/**
 * Bounce when phone OTP was explicitly required and not completed.
 *
 * - `false` → must verify (new signup with phone)
 * - `true` / `null` / `undefined` → do not bounce (grandfathered or no phone)
 */
export function shouldBounceUnverifiedPhone(
  pathname: string | null | undefined,
  phoneNumberVerified: boolean | null | undefined,
): boolean {
  return (
    phoneNumberVerified === false &&
    !PHONE_UNVERIFIED_ALLOWED.has(pathname ?? "")
  );
}
